import { toFormData } from "../../utils/parsers";
import { SignUpPayload, UserProfile } from "../../types/user.types";
import { thunkAction } from "../../utils/actionHelpers";
import * as types from "./auth.actionTypes";
import { Subscription } from "types/subscription";
import { Coupon } from "types/coupon";

export const getUserProfile = () =>
  thunkAction.GET({
    endpoint: "v3/me",
    flow: {
      request: () => ({
        type: types.GET_USER_PROFILE_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.GET_USER_PROFILE_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.GET_USER_PROFILE_FAILURE,
      }),
    },
  });

export const getUserMembership = () =>
  thunkAction.POST({
    endpoint: "v4/mindset/subscription/check-unlock",
    flow: {
      request: () => ({
        type: types.GET_USER_MEMBERSHIP_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.GET_USER_MEMBERSHIP_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.GET_USER_MEMBERSHIP_FAILURE,
      }),
    },
  });

//* User Subscription
export const getUserMembershipDetails = () =>
  thunkAction.GET({
    endpoint: "v4/revenuecat/subscription",
    flow: {
      request: () => ({
        type: types.GET_USER_MEMBERSHIP_DETAILS_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.GET_USER_MEMBERSHIP_DETAILS_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.GET_USER_MEMBERSHIP_DETAILS_FAILURE,
      }),
    },
  });

export const login = (params: { email: string; password: string }) =>
  thunkAction.POST({
    endpoint: "v3/sign-in",
    flow: {
      request: () => ({
        type: types.LOGIN_REQUEST,
      }),
      success: (data) => ({
        type: types.LOGIN_SUCCESS,
        payload: {
          ...data,
          origin: "email",
        },
      }),
      failure: () => ({
        type: types.LOGIN_FAILURE,
      }),
    },
    params,
  });

export const loginBySocial = (params: {
  socialiteToken: string;
  socialiteProvider: string;
}) =>
  thunkAction.POST({
    endpoint: "v3/link-social/sign-in",
    flow: {
      request: () => ({
        type: types.LOGIN_REQUEST,
      }),
      success: (data) => ({
        type: types.LOGIN_SUCCESS,
        payload: {
          ...data,
          origin: params.socialiteProvider,
        },
      }),
      failure: () => ({
        type: types.LOGIN_FAILURE,
      }),
    },
    params,
  });

export const signupBySocial = (params: SignUpPayload) =>
  thunkAction.POST({
    endpoint: "v3/link-social/sign-up",
    flow: {
      request: () => ({
        type: types.SIGNUP_REQUEST,
      }),
      success: (data) => ({
        type: types.SIGNUP_SUCCESS,
        payload: {
          ...data,
          origin: params.socialiteProvider,
        },
      }),
      failure: () => ({
        type: types.SIGNUP_FAILURE,
      }),
    },
    params: toFormData({
      ...params,
    }),
  });

export const logout = () => ({
  type: types.LOG_OUT,
});

export const checkEmail = (params: { email: string }) =>
  thunkAction.POST({
    endpoint: "v3/check-email",
    flow: {
      request: () => ({
        type: types.CHECK_EMAIL_REQUEST,
      }),
      success: (data) => ({
        type: types.CHECK_EMAIL_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.CHECK_EMAIL_FAILURE,
      }),
    },
    params,
  });

export const checkEmailAndPassword = (params: {
  email: string;
  password: string;
}) => {
  return thunkAction.POST({
    endpoint: "v3/sign-up/verify-email-psw",
    flow: {
      request: () => ({
        type: types.CHECK_EMAIL_AND_PASSWORD_REQUEST,
      }),
      success: (data) => ({
        type: types.CHECK_EMAIL_AND_PASSWORD_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.CHECK_EMAIL_AND_PASSWORD_FAILURE,
      }),
    },
    params,
  });
};

export const checkUsername = (params: { username }) =>
  thunkAction.POST({
    endpoint: "v3/check-username",
    flow: {
      request: () => ({
        type: types.CHECK_USERNAME_REQUEST,
      }),
      success: (data) => ({
        type: types.CHECK_USERNAME_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.CHECK_USERNAME_FAILURE,
      }),
    },
    params,
  });

export const signup = (params: SignUpPayload) => {
  const formData = new FormData();
  Object.entries(params).forEach(([key, value]) => {
    formData.append(key, value);
  });
  return thunkAction.POST({
    endpoint: "v3/sign-up",
    flow: {
      request: () => ({
        type: types.SIGNUP_REQUEST,
      }),
      success: (data) => ({
        type: types.SIGNUP_SUCCESS,
        payload: {
          ...data,
          origin: "email",
        },
      }),
      failure: () => ({
        type: types.SIGNUP_FAILURE,
      }),
    },
    params: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const resetPassword = (email: string) => {
  return thunkAction.POST({
    endpoint: "v3/forgot-psw/email-verify",
    flow: {
      request: () => ({
        type: types.RESET_PASSWORD_REQUEST,
      }),
      success: (data) => ({
        type: types.RESET_PASSWORD_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.RESET_PASSWORD_FAILURE,
      }),
    },
    params: {
      email,
    },
  });
};

export const setUserProfile = ({
  userProfile,
  isMembership,
  subscriptionDetails,
  coupon,
}: {
  userProfile: UserProfile;
  isMembership?: number;
  subscriptionDetails: null | Subscription;
  coupon: null | Coupon;
}) => ({
  type: types.SET_USER_PROFILE,
  payload: {
    userProfile,
    isMembership,
    subscriptionDetails,
    coupon,
  },
});

export const createStripeSession = (productId: string) => {
  return thunkAction.POST({
    endpoint: `v4/braintree/${productId}/session`,
    params: {
      successUrl: `${window.location.origin}/membership`,
      cancelUrl: window.location.href,
    },
    flow: {
      request: () => ({
        type: types.CREATE_STRIPE_SESSION_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.CREATE_STRIPE_SESSION_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.CREATE_STRIPE_SESSION_FAILURE,
      }),
    },
  });
};

export const createPaymentIntent = (productCode, params) => {
  return thunkAction.POST({
    endpoint: `v4/stripe/${productCode}/create-subscription`,
    flow: {
      request: () => ({
        type: types.CREATE_STRIPE_PAYMENT_INTENT_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.CREATE_STRIPE_PAYMENT_INTENT_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.CREATE_STRIPE_PAYMENT_INTENT_FAILURE,
      }),
    },
    params: {
      ...params,
    },
  });
};

export const checkCouponDetails = (couponCode) => {
  return thunkAction.POST({
    endpoint: `v4/stripe/coupon/${couponCode}`,
    flow: {
      request: () => ({
        type: types.GET_COUPON_DETAILS_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.GET_COUPON_DETAILS_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.GET_COUPON_DETAILS_FAILURE,
      }),
    },
  });
};

export const setCoupon = (coupon: null | Coupon) => ({
  type: types.SET_COUPON,
  payload: coupon,
});

export const updateProfile = (params) =>
  thunkAction.POST({
    endpoint: "v3/me",
    params: params,
    flow: {
      request: () => ({
        type: types.UPDATE_PROFILE_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.UPDATE_PROFILE_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.UPDATE_PROFILE_FAILURE,
      }),
    },
  });

export const updateUserAvatar = (params) =>
  thunkAction.POST({
    endpoint: "v3/me/update-avatar",
    params: params,
    flow: {
      request: () => ({
        type: types.UPDATE_USER_AVATAR_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.UPDATE_USER_AVATAR_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.UPDATE_USER_AVATAR_FAILURE,
      }),
    },
  });

export const updateUserEmail = (params) =>
  thunkAction.POST({
    endpoint: "v3/me/update-email",
    params: params,
    flow: {
      request: () => ({
        type: types.UPDATE_USER_EMAIL_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.UPDATE_USER_EMAIL_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.UPDATE_USER_EMAIL_FAILURE,
      }),
    },
  });

export const updateUserPassword = (params) =>
  thunkAction.POST({
    endpoint: "v3/me/update-psw",
    params: params,
    flow: {
      request: () => ({
        type: types.UPDATE_USER_PASSWORD_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.UPDATE_USER_PASSWORD_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.UPDATE_USER_PASSWORD_FAILURE,
      }),
    },
  });

export const paymentUnsubscribe = (store: string) => {
  const url =
    store?.toLowerCase() === "stripe"
      ? "v4/stripe/cancel-renewal"
      : "v4/braintree/subscription/cancel-renewal";

  return thunkAction.POST({
    endpoint: url,
    flow: {
      request: () => ({
        type: types.PAYMENT_UNSUBSCRIBE_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.PAYMENT_UNSUBSCRIBE_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.PAYMENT_UNSUBSCRIBE_FAILURE,
      }),
    },
  });
};

export const paymentResumeRenewal = (store: string) => {
  const url =
    store?.toLowerCase() === "stripe"
      ? "v4/stripe/resume-renewal"
      : "v4/braintree/subscription/resume-renewal";

  return thunkAction.POST({
    endpoint: url,
    flow: {
      request: () => ({
        type: types.PAYMENT_RESUME_RENEWAL_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.PAYMENT_RESUME_RENEWAL_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.PAYMENT_RESUME_RENEWAL_FAILURE,
      }),
    },
  });
};

export const userDeleteAccount = () =>
  thunkAction.DELETE({
    endpoint: "v4/me",
    flow: {
      request: () => ({
        type: types.USER_DELETE_ACCOUNT_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.USER_DELETE_ACCOUNT_SUCCESS,
        payload: true,
      }),
      failure: () => ({
        type: types.USER_DELETE_ACCOUNT_FAILURE,
      }),
    },
  });

export const updateUserPreferLanguage = (params) =>
  thunkAction.POST({
    endpoint: "v4/settings/language",
    params: params,
    flow: {
      request: () => ({
        type: types.USER_CHANGE_LANGUAGE_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.USER_CHANGE_LANGUAGE_SUCCESS,
        payload: {
          ...data,
        },
      }),
      failure: () => ({
        type: types.USER_CHANGE_LANGUAGE_FAILURE,
      }),
    },
  });

export const toggleDeleteAccountSuccessPopup = (params) => ({
  type: types.TOGGLE_DELETE_ACCOUNT_POPUP,
  payload: params,
});

export const fetchLikedContent = () =>
  thunkAction.GET({
    endpoint: `${process.env.NEXT_PUBLIC_NODE_API_URL}/v1/content-highlights/likes`,
    flow: {
      request: () => ({
        type: types.FETCH_LIKED_CONTENT_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.FETCH_LIKED_CONTENT_SUCCESS,
        payload: data,
      }),
      failure: () => ({
        type: types.FETCH_LIKED_CONTENT_FAILURE,
      }),
    },
  });
export const likeContent = (id: number) =>
  thunkAction.POST({
    endpoint: `${process.env.NEXT_PUBLIC_NODE_API_URL}/v1/content-highlights/${id}/like`,
    flow: {
      request: () => ({
        type: types.LIKE_CONTENT_REQUEST,
      }),
      success: () => ({
        type: types.LIKE_CONTENT_SUCCESS,
        payload: {
          id,
        },
      }),
      failure: () => ({
        type: types.LIKE_CONTENT_FAILURE,
      }),
    },
  });
export const unlikeContent = (id: number) =>
  thunkAction.POST({
    endpoint: `${process.env.NEXT_PUBLIC_NODE_API_URL}/v1/content-highlights/${id}/unlike`,
    flow: {
      request: () => ({
        type: types.UNLIKE_CONTENT_REQUEST,
      }),
      success: () => ({
        type: types.UNLIKE_CONTENT_SUCCESS,
        payload: {
          id,
        },
      }),
      failure: () => ({
        type: types.UNLIKE_CONTENT_FAILURE,
      }),
    },
  });

export const fetchLikedPhotos = () =>
  thunkAction.GET({
    endpoint: `${process.env.NEXT_PUBLIC_NODE_API_URL}/v1/photos/likes`,
    flow: {
      request: () => ({
        type: types.FETCH_LIKED_PHOTOS_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.FETCH_LIKED_PHOTOS_SUCCESS,
        payload: data,
      }),
      failure: () => ({
        type: types.FETCH_LIKED_PHOTOS_FAILURE,
      }),
    },
  });
export const likePhoto = (id: number) =>
  thunkAction.POST({
    endpoint: `${process.env.NEXT_PUBLIC_NODE_API_URL}/v1/photos/${id}/like`,
    flow: {
      request: () => ({
        type: types.LIKE_PHOTO_REQUEST,
      }),
      success: () => ({
        type: types.LIKE_PHOTO_SUCCESS,
        payload: {
          id,
        },
      }),
      failure: () => ({
        type: types.LIKE_PHOTO_FAILURE,
      }),
    },
  });
export const unlikePhoto = (id: number) =>
  thunkAction.POST({
    endpoint: `${process.env.NEXT_PUBLIC_NODE_API_URL}/v1/photos/${id}/unlike`,
    flow: {
      request: () => ({
        type: types.UNLIKE_PHOTO_REQUEST,
      }),
      success: () => ({
        type: types.UNLIKE_PHOTO_SUCCESS,
        payload: {
          id,
        },
      }),
      failure: () => ({
        type: types.UNLIKE_PHOTO_FAILURE,
      }),
    },
  });

export const getBrainTreeClientToken = () =>
  thunkAction.POST({
    endpoint: `v4/braintree/client-token`,
    flow: {
      request: () => ({
        type: types.GET_BRAINTREE_CLIENT_TOKEN_REQUEST,
      }),
      success: (data) => ({
        type: types.GET_BRAINTREE_CLIENT_TOKEN_SUCCESS,
        payload: data,
      }),
      failure: () => ({
        type: types.GET_BRAINTREE_CLIENT_TOKEN_FAILURE,
      }),
    },
  });

export const createBrainTreeSubscription = (
  planId: string,
  nonce: string,
  couponId?: string
) =>
  thunkAction.POST({
    endpoint: `v4/braintree/create-subscription`,
    flow: {
      request: () => ({
        type: types.CREATE_BRAINTREE_SUBSCRIPTION_REQUEST,
      }),
      success: (data) => ({
        type: types.CREATE_BRAINTREE_SUBSCRIPTION_SUCCESS,
        payload: data,
      }),
      failure: () => ({
        type: types.CREATE_BRAINTREE_SUBSCRIPTION_FAILURE,
      }),
    },
    params: {
      plainId: planId,
      paymentMethodNonce: nonce,
      discountId: couponId,
    },
  });

export const getSignedCookies = () =>
  thunkAction.GET({
    endpoint: `v4/security/aws-signed-cookies`,
    flow: {
      request: () => ({
        type: types.GET_SIGNED_COOKIES_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.GET_SIGNED_COOKIES_SUCCESS,
        payload: data,
      }),
      failure: () => ({
        type: types.GET_SIGNED_COOKIES_FAILURE,
      }),
    },
  });

export const getMindsetIsPaid = (mindsetSlug: string) =>
  thunkAction.POST({
    endpoint: `v4/mindset/${mindsetSlug}/check-unlock`,
    flow: {
      request: () => ({
        type: types.GET_MINDSET_IS_PAID_REQUEST,
      }),
      // @ts-ignore
      success: ({ status }) => ({
        type: types.GET_MINDSET_IS_PAID_SUCCESS,
        payload: {
          isPaid: status,
          slug: mindsetSlug,
        },
      }),
      failure: () => ({
        type: types.GET_MINDSET_IS_PAID_FAILURE,
      }),
    },
  });

export const getStripePromoCodeInfo = (code: string) =>
  thunkAction.GET({
    endpoint: `v4/stripe/promotion/${code}`,
    flow: {
      request: () => ({
        type: types.GET_STRIPE_PROMO_CODE_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.GET_STRIPE_PROMO_CODE_SUCCESS,
        payload: data,
      }),
      failure: (error) => ({
        type: types.GET_STRIPE_PROMO_CODE_FAILURE,
        payload: error?.message,
      }),
    },
  });

export const resetStripePromoCodeInfo = () => {
  return {
    type: types.RESET_STRIPE_PROMO_CODE,
  };
};

export const createStripeSetupIntent = () => {
  return thunkAction.POST({
    endpoint: `v4/stripe/create-setup-intent`,
    flow: {
      request: () => ({
        type: types.CREATE_STRIPE_SETUP_INTENT_REQUEST,
      }),
      // @ts-ignore
      success: (data) => ({
        type: types.CREATE_STRIPE_SETUP_INTENT_SUCCESS,
        payload: data,
      }),
      failure: (error) => ({
        type: types.CREATE_STRIPE_SETUP_INTENT_FAILURE,
        data: error?.message,
      }),
    },
  });
};

export const toggleShowPromoCodeInput = () => ({
  type: types.TOGGLE_SHOW_PROMO_CODE,
});

export const removePromoCode = () => ({
  type: types.REMOVE_PROMO_CODE,
});
